import React, { createContext, useContext, useState, useCallback } from 'react';
import { Answer, SurveyResponse, Language } from '../types';

interface SurveyContextType {
  currentQuestion: number;
  answers: Answer[];
  isComplete: boolean;
  totalQuestions: number;
  setAnswer: (questionIndex: number, value: string) => void;
  nextQuestion: () => void;
  previousQuestion: () => void;
  goToQuestion: (questionIndex: number) => void;
  resetSurvey: () => void;
  completeSurvey: () => SurveyResponse;
  canGoNext: boolean;
  canGoPrevious: boolean;
}

const SurveyContext = createContext<SurveyContextType | undefined>(undefined);

interface SurveyProviderProps {
  children: React.ReactNode;
  totalQuestions: number;
  language: Language;
}

export function SurveyProvider({
  children,
  totalQuestions,
  language,
}: SurveyProviderProps) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [isComplete, setIsComplete] = useState(false);

  const setAnswer = useCallback((questionIndex: number, value: string) => {
    setAnswers((prev) => {
      const newAnswers = [...prev];
      const existingIndex = newAnswers.findIndex(
        (a) => a.questionId === questionIndex
      );

      const newAnswer: Answer = {
        questionId: questionIndex,
        value,
      };

      if (existingIndex >= 0) {
        newAnswers[existingIndex] = newAnswer;
      } else {
        newAnswers.push(newAnswer);
      }

      return newAnswers;
    });
  }, []);

  const nextQuestion = useCallback(() => {
    if (currentQuestion < totalQuestions - 1) {
      setCurrentQuestion((prev) => prev + 1);
    } else {
      setIsComplete(true);
    }
  }, [currentQuestion, totalQuestions]);

  const previousQuestion = useCallback(() => {
    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
    }
  }, [currentQuestion]);

  const goToQuestion = useCallback(
    (questionIndex: number) => {
      if (questionIndex >= 0 && questionIndex < totalQuestions) {
        setCurrentQuestion(questionIndex);
        setIsComplete(false);
      }
    },
    [totalQuestions]
  );

  const resetSurvey = useCallback(() => {
    setCurrentQuestion(0);
    setAnswers([]);
    setIsComplete(false);
  }, []);

  const completeSurvey = useCallback((): SurveyResponse => {
    const response: SurveyResponse = {
      answers,
      timestamp: new Date(),
      language,
    };
    return response;
  }, [answers, language]);

  const canGoNext =
    answers.some((a) => a.questionId === currentQuestion) || isComplete;
  const canGoPrevious = currentQuestion > 0;

  const value: SurveyContextType = {
    currentQuestion,
    answers,
    isComplete,
    totalQuestions,
    setAnswer,
    nextQuestion,
    previousQuestion,
    goToQuestion,
    resetSurvey,
    completeSurvey,
    canGoNext,
    canGoPrevious,
  };

  return (
    <SurveyContext.Provider value={value}>{children}</SurveyContext.Provider>
  );
}

export function useSurvey(): SurveyContextType {
  const context = useContext(SurveyContext);
  if (context === undefined) {
    throw new Error('useSurvey must be used within a SurveyProvider');
  }
  return context;
}
